/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Notifications and the WhatsApp unread count for the v2 header, using the
 * same endpoints and shared WebSocket state as the classic navbar
 * (examples/Navbars/DashboardNavbar). Under v2 that navbar renders only a
 * breadcrumb row, so these fetches happen here instead -- once, not per page.
 */

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "auth/Auth";
import { useWebSocket } from "context/WebSocketContext";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import * as whatsappNotifySound from "whatsapp/notifySound";

export function useNotifications() {
  const { axiosInstance, user, token } = useAuth();
  const { notifications, setNotifications } = useWebSocket();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!user?.id || !token) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/notifications/");
      setNotifications(data);
      setError(null);
    } catch (e) {
      setError("Could not load notifications.");
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, setNotifications, token, user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const markRead = useCallback(
    async (id) => {
      await axiosInstance.post("/notifications/mark_as_read/", { id });
      setNotifications((prev) => (prev || []).map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    },
    [axiosInstance, setNotifications]
  );

  const markAllRead = useCallback(async () => {
    await axiosInstance.post("/notifications/mark_all_read");
    setNotifications((prev) => (prev || []).map((n) => ({ ...n, is_read: true })));
  }, [axiosInstance, setNotifications]);

  const list = Array.isArray(notifications) ? notifications : [];
  return { notifications: list, unread: list.filter((n) => !n.is_read).length, loading, error, refresh, markRead, markAllRead };
}

export function useWhatsappUnread() {
  const { axiosInstance, user, token } = useAuth();
  const { whatsappInboxEvent, wsConnected } = useWebSocket();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const enabled = !entitlementsLoading && isModuleEnabled("WHATSAPP");
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!user?.id || !token || !enabled) {
      setCount(0);
      return;
    }
    try {
      const { data } = await axiosInstance.get("/whatsapp/inbox/unread-count");
      setCount(data?.count || 0);
    } catch {
      setCount(0);
    }
  }, [axiosInstance, enabled, token, user?.id]);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return undefined;
    }
    fetchCount();
    const interval = setInterval(fetchCount, wsConnected ? 120000 : 15000);
    return () => clearInterval(interval);
  }, [enabled, fetchCount, wsConnected]);

  useEffect(() => {
    if (enabled) whatsappNotifySound.bindAutoPrime();
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !whatsappInboxEvent) return;
    fetchCount();
    whatsappNotifySound.playInboundOnce(whatsappInboxEvent);
  }, [enabled, whatsappInboxEvent, fetchCount]);

  return { enabled, count };
}
